import { z } from 'zod';
import { PERMISSIONS } from '../permissions';

/** What a tool does. Upcased from the code token for display; German is i18n. */
export const ToolCatalogKindSchema = z.enum(['QUERY', 'COMMAND']);

export type ToolCatalogKind = z.infer<typeof ToolCatalogKindSchema>;

/**
 * How much a command may do unattended. `null` on a query — a read has no side
 * effect to classify, which is a rule the catalogue's own CI gate enforces
 * rather than a convention this schema merely permits.
 */
export const ToolSideEffectClassSchema = z.enum(['AUTO_WITH_AUDIT', 'APPROVAL_REQUIRED']);

export type ToolSideEffectClassView = z.infer<typeof ToolSideEffectClassSchema>;

/**
 * `DEPRECATED` means replaced, not gone: the entry stays so an allowlist naming
 * it keeps resolving until its skill is migrated (IMM-436.4, AC 173).
 */
export const ToolCatalogStatusSchema = z.enum(['ACTIVE', 'DEPRECATED']);

export type ToolCatalogStatus = z.infer<typeof ToolCatalogStatusSchema>;

/**
 * One tool an allowlist may name (IMM-436.4, AC 171).
 *
 * `toolId` is the stable identity allowlists store and the thing to send back;
 * `name` is what the model sees and may be re-tuned at any time, so nothing
 * durable should ever be keyed by it.
 */
export const ToolCatalogEntrySchema = z.object({
  toolId: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  kind: ToolCatalogKindSchema,
  sideEffectClass: ToolSideEffectClassSchema.nullable(),
  requiredPermission: z.enum(PERMISSIONS),
  status: ToolCatalogStatusSchema,
});

export type ToolCatalogEntryView = z.infer<typeof ToolCatalogEntrySchema>;

export const ListToolCatalogQuerySchema = z.object({
  /**
   * Include replaced tools. Off for assembling a NEW allowlist — nobody should
   * be steered onto a deprecated tool — and on when resolving an EXISTING one,
   * so a configured skill's entries are shown rather than silently dropped.
   */
  includeDeprecated: z.stringbool().optional(),
});

export type ListToolCatalogQuery = z.infer<typeof ListToolCatalogQuerySchema>;

export const ListToolCatalogResponseSchema = z.object({
  data: z.array(ToolCatalogEntrySchema),
});

export type ListToolCatalogResponse = z.infer<typeof ListToolCatalogResponseSchema>;
